<template>
  <div>
    <v-form
      ref="cassationReasonForm"
      class="cassation-reason-form"
      @submit.prevent="handleAdd"
    >
      <v-row>
        <v-col cols="6">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="reason.reasonId"
            :label="local.caseRelatedRequest.cassationRequest.selectReasons"
            :items="availableReasons"
            item-value="reasonId"
            item-text="label"
            :disabled="disabled"
            validates="required"
            @change="getLabelReason"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row>
        <naj-box :title="local.caseRelatedRequest.cassationRequest.positionOfJudgementText">
          <template v-slot:header>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  color="primary"
                  size="20"
                  style="cursor: pointer;"
                  v-on="on"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ local.caseRelatedRequest.cassationRequest.positionOfJudgementHint }}</span>
            </v-tooltip>
          </template>
          <div v-if="reason.subject">
            <v-row v-if="!isFullJudgmentReason">
              <v-col cols="10">
                <label class="section-title">{{ reason.subject.judgementSectionName }}</label>
              </v-col>
              <v-col
                v-if="!disabled"
                cols="2"
              >
                <v-btn
                  icon
                  @click="editSubject"
                >
                  <v-icon color="primary">
                    mdi-lead-pencil
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  @click="selectSubjectToDelete"
                >
                  <v-icon color="red">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="reason.subject.judgementHighlightedText"
                cols="12"
              >
                <naj-input
                  disabled
                  :type-id="formFieldType.textarea"
                  :value.sync="reason.subject.judgementHighlightedText"
                  validates="required|max:2000|allowed_special_character"
                  rows="5"
                ></naj-input>
              </v-col>
            </v-row>
          </div>
          <v-row v-if="!isFullJudgmentReason">
            <v-col cols="12">
              <naj-button
                color="primary"
                :disabled="disabled || !!reason.subject"
                @click="openJudgementTextModal"
              >
                {{ local.caseRelatedRequest.cassationRequest.selectJudgementText }}
              </naj-button>
            </v-col>
          </v-row>
        </naj-box>
      </v-row>

      <v-row>
        <v-col cols="12">
          <naj-input
            class="label-tooltip"
            :type-id="formFieldType.textarea"
            :value.sync="reason.statementOfViolation"
            :label="local.caseRelatedRequest.cassationRequest.statementOfViolation"
            :disabled="disabled"
            :tooltip-content="local.caseRelatedRequest.cassationRequest.statementOfViolationHint"
            validates="required|max:2000|allowed_special_character"
            rows="4"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <naj-input
            class="label-tooltip"
            :type-id="formFieldType.textarea"
            :value.sync="reason.violationEffect"
            :label="local.caseRelatedRequest.cassationRequest.violationEffect"
            :disabled="disabled"
            :tooltip-content="local.caseRelatedRequest.cassationRequest.violationEffectHint"
            validates="required|max:2000|allowed_special_character"
            rows="4"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="6"
          class="my-auto stop-execution-radio-col"
        >
          <naj-input
            :type-id="formFieldType.radioButton"
            :value.sync="reason.hasObjectionedForAppeal"
            :label="local.caseRelatedRequest.cassationRequest.hasObjectionedForAppeal"
            :items="yesOrNo.map(c=> ({...c,id:Boolean(c.id)}))"
            validates="required"
            item-value="id"
            item-text="name"
            :disabled="disabled"
            @change="hasObjectionedChangeHandler"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row v-if="reason.hasObjectionedForAppeal == yesOrNoEnumBooleanMap[yesOrNoEnumBoolean.yes]">
        <v-col cols="12">
          <naj-input
            class="label-tooltip"
            :type-id="formFieldType.textarea"
            :value.sync="reason.reasonForObjectionWithSameReasonAgain"
            :label="local.caseRelatedRequest.cassationRequest.reasonForObjectionWithSameReasonAgain"
            :disabled="disabled"
            :tooltip-content="local.caseRelatedRequest.cassationRequest.reasonForObjectionWithSameReasonAgainHint"
            :validates="`${
              reason.hasObjectionedForAppeal == yesOrNoEnumBooleanMap[yesOrNoEnumBoolean.yes] ? 'required|' : ''
            }max:2000|allowed_special_character`"
            rows="4"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row v-if="reason.hasObjectionedForAppeal == yesOrNoEnumBooleanMap[yesOrNoEnumBoolean.no]">
        <v-col cols="12">
          <naj-input
            :type-id="formFieldType.textarea"
            :value.sync="reason.reasonForNotMentionAtAppeal"
            :label="local.caseRelatedRequest.cassationRequest.reasonForNotMentionAtAppeal"
            :disabled="disabled"
            :validates="`${
              reason.hasObjectionedForAppeal == yesOrNoEnumBooleanMap[yesOrNoEnumBoolean.no] ? 'required|' : ''
            }max:2000|allowed_special_character`"
            rows="4"
          >
          </naj-input>
        </v-col>
      </v-row>
      <v-row v-if="!disabled">
        <v-col cols="12">
          <naj-button
            color="secondary"
            @click="handleCancel"
          >
            {{ local.common.cancel }}
          </naj-button>
          <naj-button
            type="submit"
            :loading="loading"
          >
            {{ local.caseRelatedRequest.cassationRequest.saveChanges }}
          </naj-button>
        </v-col>
      </v-row>
    </v-form>

    <interpretation-judgement-subjects
      :show-modal="showJudgementSubjectsModal"
      :title="local.caseRelatedRequest.cassationRequest.subjectOfCassationRequest"
      :form-model="formModel"
      :selected-judgment="selectedJudgment"
      :value="reason.subject || {}"
      @addSubject="addSubject"
      @closeModal="handleCloseModal"
    >
    </interpretation-judgement-subjects>
  </div>
</template>

<script>
import { yesOrNoEnumBoolean, yesOrNoEnumBooleanMap } from '@/helpers/enum';
import InterpretationJudgementSubjects from './interpretation-judgement-subjects';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState } = createNamespacedHelpers('LookupsModule');
export default {
  name: 'CassationformModel',
  inject: ['$validator'],
  components: { InterpretationJudgementSubjects },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    reason: {
      type: Object,
      default: () => ({}),
    },
    availableReasons: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectedJudgment: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      yesOrNoEnumBoolean,
      yesOrNoEnumBooleanMap,
      judgementTextModal: false,
      loading: false,
      showJudgementSubjectsModal: false,
    };
  },
  computed: {
    ...mapLUTState(['yesOrNo']),
    selectedReason() {
      return this.availableReasons.find(r => r.reasonId === this.reason.reasonId);
    },
    isFullJudgmentReason() {
      return this.selectedReason && [72, 73].includes(this.selectedReason.reasonId);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.positionLabelTooltips();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.positionLabelTooltips();
    });
  },
  methods: {
    addSubject(details) {
      this.$set(this.reason, 'subject', details);
      this.showJudgementSubjectsModal = false;
    },
    handleCancel() {
      this.$emit('cancel');
    },
    handleCloseModal() {
      this.showJudgementSubjectsModal = false;
    },
    openJudgementTextModal() {
      if (!this.selectedJudgment.judgementCode) {
        this.toaster.error(this.local.caseRelatedRequest.correctionJudgementRequest.selectJudgementErrorMsg);
        return;
      }
      this.showJudgementSubjectsModal = true;
    },
    closeJudgementTextModal() {
      this.judgementTextModal = false;
    },
    getLabelReason() {
      const selectedReason = this.availableReasons.find(reason => reason.reasonId === this.reason.reasonId);
      this.reason.label = selectedReason ? selectedReason.label : '';
      if (this.isFullJudgmentReason) {
        this.$set(this.reason, 'subject', { judgementHighlightedText: this.local.caseRelatedRequest.cassationRequest.fullJudgmentObjection });
      } else {
        this.$set(this.reason, 'subject', null);
      }
    },
    handleAdd() {
      if (this.reason.subject == null) {
        this.toaster.error(this.local.caseRelatedRequest.cassationRequest.subjectRequired);
        return;
      }
      if (this.$refs.cassationReasonForm) {
        this.$refs.cassationReasonForm.$validator.validateAll().then((valid) => {
          if (valid) {
            this.$emit('add', this.reason);
          }
        });
      }
    },
    hasObjectionedChangeHandler() {
      this.reason.reasonForObjectionWithSameReasonAgain = '';
      this.reason.reasonForNotMentionAtAppeal = '';
    },
    removeSubject() {
      this.reason.subject = {};
    },
    editSubject() {
      this.showJudgementSubjectsModal = true;
    },
    selectSubjectToDelete() {
      this.$set(this.reason, 'subject', null);
    },
    positionLabelTooltips() {
      const containers = this.$el.querySelectorAll('.label-tooltip');
      containers.forEach((container) => {
        const label = container.querySelector('.v-label');
        const tooltipBtn = container.querySelector('.tooltip-button');
        if (label && tooltipBtn) {
          const labelRect = label.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          tooltipBtn.style.position = 'absolute';
          tooltipBtn.style.top = (labelRect.top - containerRect.top) + 'px';
          tooltipBtn.style.right = (containerRect.right - labelRect.left + 5) + 'px';
          tooltipBtn.style.padding = '0';
          tooltipBtn.style.zIndex = '10';
        }
      });
    },

    setSelectedJudgment() {
      this.$set(this.selectedJudgment, 'judgmentCode', this.formModel.case.judgmentCode);
      this.activeJudgement = this.formModel.case.judgmentCode;
    },
  },
};
</script>

<style lang="scss" scoped>
.stop-execution-radio-col ::v-deep .v-input--radio-group .options-list {
  display: flex !important;
  flex-direction: row !important;
}

::v-deep .naj-box-header {
  > div {
    display: contents;
  }
  .header-icon {
    order: -1;
  }
  .header-slot {
    order: 1;
    margin-right: 8px;
    display: inline-flex;
    align-items: center;
  }
}

.section-title {
  font-size: 22px;
  color: var(--app-primary-color);
}

::v-deep .label-tooltip.input-container {
  position: relative;

  .tooltip-button .v-icon {
    font-size: 20px;
    color: var(--app-primary-color, #1976d2);

    &::before {
      content: '\F02FD';
    }
  }
}
</style>
